import React, {useMemo, useState} from 'react';

import {
  Button,
  Heading,
  Icon,
  IconButton,
  SegmentedControl,
  Skeleton,
  Tab
} from '@albert-io/atomic';
import {useIsFeatureFlagEnabled} from 'client/components/FeatureFlag/featureFlag.utils';
import ManageRosterContext from 'client/Classrooms/Classroom/Teacher/ManageRoster/ManageRoster.context';
import manageRosterModalCollection from 'client/Classrooms/Classroom/Teacher/ManageRoster/manageRosterModalCollection';
import PeopleContext from 'client/Classrooms/Classroom/Teacher/People/people.context';
import {LIST_ROW_CLICK_ACTION_FLAG} from 'client/TeacherDashboard/constants';

import {CenteredColumn} from '../../components/CenteredColumn';
import {ErrorRetryState} from '../../components/ErrorRetryState';
import {SEGMENTS} from './constants';
import {useClassContentRevealed, useReportClassContentReady} from '../useClassContentReady';
import {StudentsTab} from './StudentsTab';
import type {StudentSegment} from './constants';
import {TeachersTab} from './TeachersTab';
import {usePeopleRoster} from './usePeopleRoster';

type PeopleTab = 'students' | 'teachers';

// Matches the loaded layout (57px tab bar, "Students" heading, controls, ~69px roster rows) so the
// reveal fills in place.
const PeopleSkeleton = () => (
  <div aria-busy='true' className='flex flex-col'>
    <span className='sr-only' role='status'>
      Loading roster…
    </span>
    <nav aria-hidden='true' className='-mx-x4 border-border-primary px-x4 flex border-b'>
      <Skeleton>
        <Tab as='button'>
          <Icon className='mr-x1' icon='users' iconStyle='regular' size='inherit' />
          Students
        </Tab>
      </Skeleton>
      <Skeleton>
        <Tab as='button'>
          <Icon className='mr-x1' icon='person-chalkboard' iconStyle='regular' size='inherit' />
          Teachers
        </Tab>
      </Skeleton>
    </nav>
    <CenteredColumn className='gap-x3 pt-x4 flex flex-col'>
      <Heading as='h2' className='m-x0' size='s'>
        Students
      </Heading>
      <div aria-hidden='true' className='gap-x2 flex flex-wrap items-center justify-between'>
        <Skeleton>
          <SegmentedControl options={SEGMENTS} selectedIdx={0} />
        </Skeleton>
        <div className='gap-x1 flex items-center'>
          <Skeleton>
            <IconButton
              aria-hidden
              color='secondary'
              icon='magnifying-glass'
              roundness='square'
              variant='outlined'
            />
          </Skeleton>
          <Skeleton>
            <Button>
              <Icon className='mr-x1' icon='user-plus' iconStyle='regular' />
              Invite Students
            </Button>
          </Skeleton>
        </div>
      </div>
      <div aria-hidden='true' className='flex flex-col'>
        {['r1', 'r2', 'r3', 'r4', 'r5'].map((key) => (
          <div className='gap-x2 py-x3 border-border-subtle flex items-center border-b' key={key}>
            <Skeleton className='h-x2 w-[18%] rounded-full' />
            <Skeleton className='h-x2 w-[18%] rounded-full' />
            <Skeleton className='h-x2 grow rounded-full' />
          </div>
        ))}
      </div>
    </CenteredColumn>
  </div>
);

// email | bulkResend | classCode | googleClassroom | LMS — student invite flows, rendered on demand.
const ROSTER_MODALS = manageRosterModalCollection as Record<
  string,
  React.ComponentType<{handleClose: () => void}>
>;

interface Props {
  classroomId: string;
}

export const People = ({classroomId}: Props) => {
  const roster = usePeopleRoster(classroomId);
  const [activeTab, setActiveTab] = useState<PeopleTab>('students');
  const [studentSegment, setStudentSegment] = useState<StudentSegment>('enrolled');
  // Drives the shared student-invite modals (mirrors the legacy ManageRoster container).
  const [modalToShow, setModalToShow] = useState('');

  const {classroom, isReady, hasError, reload, softReload, queries, currentTeacherId} = roster;

  // setIsReady(false) is how the reused flows signal "I changed data". Refresh in place (softReload)
  // rather than reload(): reload() drops isReady and remounts the whole view behind a spinner — a
  // jarring "page refresh" even when nothing changed (e.g. cancelling the invite-by-email modal).
  // The reused legacy roster flows (row actions, bulk-resend) read `queries` (for interest
  // invalidation + the invitations total_entries meta) and `classroom` — not the full Lists, which
  // the server-paged lists no longer fetch.
  const manageRosterContext = useMemo(
    () => ({
      queries: {
        classroom: queries.classroom,
        invitations: queries.invitations,
        students: queries.students
      },
      classroom,
      isReady,
      modalToShow,
      setIsReady: (value: boolean) => {
        if (!value) {
          softReload();
        }
      },
      setModalToShow
    }),
    [queries, classroom, isReady, modalToShow, softReload]
  );

  const peopleContext = useMemo(
    () => ({classroom, queries: {coteachers: queries.coteachers}, currentTeacherId}),
    [classroom, queries, currentTeacherId]
  );

  // Report when the roster is ready (or errors); the shell reveals the header + this tab together on
  // `revealed`, so neither shows before the other. The `!isReady` keeps the tab's own skeleton for
  // post-reveal refetches (e.g. a reload() that flips isReady), where the header stays put.
  const revealed = useClassContentRevealed();
  const {isLoading: isRowRedesignFlagLoading} = useIsFeatureFlagEnabled(LIST_ROW_CLICK_ACTION_FLAG);
  useReportClassContentReady(isReady && !isRowRedesignFlagLoading);

  if (!revealed || !isReady || isRowRedesignFlagLoading) {
    return <PeopleSkeleton />;
  }

  if (hasError || typeof classroom?.getId !== 'function') {
    return (
      <CenteredColumn className='pt-x4'>
        <ErrorRetryState copy='We couldn’t load this class’s roster.' onRetry={reload} />
      </CenteredColumn>
    );
  }

  const ModalRenderer = modalToShow ? ROSTER_MODALS[modalToShow] : null;

  return (
    <ManageRosterContext.Provider value={manageRosterContext}>
      <PeopleContext.Provider value={peopleContext}>
        <div className='flex flex-col'>
          <nav
            aria-label='People views'
            className='-mx-x4 border-border-primary px-x4 flex border-b'
          >
            <Tab
              as='button'
              isActive={activeTab === 'students'}
              onClick={() => setActiveTab('students')}
            >
              <Icon className='mr-x1' icon='users' iconStyle='regular' size='inherit' />
              Students
            </Tab>
            <Tab
              as='button'
              isActive={activeTab === 'teachers'}
              onClick={() => setActiveTab('teachers')}
            >
              <Icon className='mr-x1' icon='person-chalkboard' iconStyle='regular' size='inherit' />
              Teachers
            </Tab>
          </nav>
          {activeTab === 'students' ? (
            <StudentsTab
              classroom={classroom}
              onSegmentChange={setStudentSegment}
              onShowModal={setModalToShow}
              segment={studentSegment}
            />
          ) : (
            <CenteredColumn>
              <TeachersTab coteachers={roster.coteachers} currentTeacherId={currentTeacherId} />
            </CenteredColumn>
          )}
        </div>
        {ModalRenderer && <ModalRenderer handleClose={() => setModalToShow('')} />}
      </PeopleContext.Provider>
    </ManageRosterContext.Provider>
  );
};
